import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import useAuthStore from '../stores/authStore';
import useUserProfileStore from '../stores/userProfileStore';
import {
  USER_ROLES,
  getDefaultPathForRole,
  getRoleLabel,
  getRoleFromUserDoc,
  getStoredUserRoleFromUserDoc,
  hasRouteAccess,
} from '../utils/accessControl';

import './ProtectedRoutes.css';

const ProtectedRoute = ({ children, routeKey }) => {
  const user = useAuthStore(state => state.user);
  const setUser = useAuthStore(state => state.setUser);
  const logout = useAuthStore(state => state.logout);

  const setAdmin = useUserProfileStore(state => state.setAdmin);
  const role = useUserProfileStore(state => state.role);
  const setRole = useUserProfileStore(state => state.setRole);
  const clearAccess = useUserProfileStore(state => state.clearAccess);

  const [initializing, setInitializing] = useState(true);
  const denialAlertShownRef = useRef(false);

  useEffect(() => {
    let isSubscribed = true;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (!currentUser) {
        denialAlertShownRef.current = false;
        clearAccess();
        if (isSubscribed) {
          setInitializing(false);
        }
        return;
      }

      if (isSubscribed) {
        setInitializing(true);
      }

      try {
        const currentUserDocRef = doc(db, 'users', currentUser.uid);
        const currentUserDocSnapshot = await getDoc(currentUserDocRef);
        const currentUserDoc = currentUserDocSnapshot.data();
        const resolvedRole = getRoleFromUserDoc(currentUserDoc);
        const storedRole = getStoredUserRoleFromUserDoc(currentUserDoc);
        setRole(resolvedRole);
        setAdmin(resolvedRole === USER_ROLES.ADMIN);

        if (!resolvedRole) {
          if (!denialAlertShownRef.current) {
            const accessMessage = storedRole
              ? `Your user role is ${getRoleLabel(storedRole)}. That role does not have access to the admin panel.`
              : 'Your admin panel role is not set on your user profile. Ask an admin to set your userRole.';
            alert(accessMessage);
            denialAlertShownRef.current = true;
          }
          await logout();
        }
      } catch (error) {
        console.error('Error fetching user role from Firestore:', error);
        clearAccess();
        if (!denialAlertShownRef.current) {
          alert('Unable to read your admin panel role. Please login again.');
          denialAlertShownRef.current = true;
        }
        await logout();
      } finally {
        if (isSubscribed) {
          setInitializing(false);
        }
      }
    });

    return () => {
      isSubscribed = false;
      unsubscribe();
    };
  }, [setUser, setAdmin, setRole, clearAccess, logout]);

  if (initializing) {
    return (
      <div>
        <h1 className='protectedRoutes__loading'>Loading...</h1>
      </div>
    );
  }

  if (!user || !role) {
    return <Navigate to="/login" replace />;
  }

  if (routeKey && !hasRouteAccess(role, routeKey)) {
    return <Navigate to={getDefaultPathForRole(role)} replace />;
  }

  return children;
};

export default ProtectedRoute;
